import styled from '@emotion/styled';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { colors, fonts, breakpoints, frames } from './tokens';
import { Panel } from './Card';
import { EndSessionButton, BackButton, NextButton } from './Button';
import { VideoStrip } from './VideoStrip';

// ---------------------------------------------------------------------------------------------
// Layout mode: which Figma frame family applies to the current viewport.
//   phone     < 600px wide                         (iPhone 375x812)
//   portrait  tablet portrait                      (iPad Mini 744x1133)
//   landscape >= 1024 wide and wider than tall     (iPad Mini 1133x744, desktop - centred max-width)
// ---------------------------------------------------------------------------------------------

export const getLayoutMode = (w = window.innerWidth, h = window.innerHeight) => {
  if (w < breakpoints.phone) return 'phone';
  if (w >= breakpoints.wide && w > h) return 'landscape';
  if (w > h && h < 520) return 'landscape'; // phone / small tablet on its side
  return 'portrait';
};

export const useLayoutMode = () => {
  const [mode, setMode] = useState(() => (typeof window === 'undefined' ? 'portrait' : getLayoutMode()));
  useLayoutEffect(() => {
    const update = () => setMode(getLayoutMode());
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);
  return mode;
};

// ---------------------------------------------------------------------------------------------
// SessionShell: full-height page = header + body. Every session screen sits in one.
// ---------------------------------------------------------------------------------------------

const ShellRoot = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  height: ${({ fixed }) => (fixed ? '100dvh' : 'auto')};
  background: ${colors.pageBg};
  font-family: ${fonts.family};
  color: ${colors.text};
  -webkit-font-smoothing: antialiased;
  user-select: none;
`;

const ShellBody = styled.main`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${frames.desktopMaxWidth}px;
  margin: 0 auto;
  padding: ${({ padding }) => padding};
  box-sizing: border-box;
  @media (max-width: ${breakpoints.phone - 1}px) {
    padding: 12px;
  }
`;

/**
 * @param header  a <SessionHeader/>
 * @param fixed   lock the shell to the viewport height (in-test layout); otherwise the page scrolls
 */
export const SessionShell = ({ header, children, fixed = false, padding = '20px 24px 24px', ...rest }) => (
  <ShellRoot fixed={fixed} {...rest}>
    {header}
    <ShellBody padding={padding}>{children}</ShellBody>
  </ShellRoot>
);

// ---------------------------------------------------------------------------------------------
// Chrome context: lets any screen inside a SessionLayout set the header title, put its buttons
// in the nav row under the panel (<SessionActions/>) and hide/show the video feed.
// ---------------------------------------------------------------------------------------------

const ChromeContext = createContext(null);

export const SessionChromeProvider = ({ children, initialTitle = '' }) => {
  const [title, setTitle] = useState(initialTitle);
  const [actionsNode, setActionsNode] = useState(null);
  const [videoHidden, setVideoHidden] = useState(false);
  const [autoHidden, setAutoHidden] = useState(0); // >0 while a screen requests the feed hidden
  const requestVideoHidden = useCallback(() => {
    setAutoHidden((n) => n + 1);
    return () => setAutoHidden((n) => Math.max(0, n - 1));
  }, []);
  const value = useMemo(
    () => ({ title, setTitle, actionsNode, setActionsNode, videoHidden: videoHidden || autoHidden > 0, setVideoHidden, requestVideoHidden, userVideoHidden: videoHidden }),
    [title, actionsNode, videoHidden, autoHidden, requestVideoHidden],
  );
  return <ChromeContext.Provider value={value}>{children}</ChromeContext.Provider>;
};

const noopChrome = { title: '', setTitle: () => {}, actionsNode: null, setActionsNode: () => {}, videoHidden: false, setVideoHidden: () => {}, requestVideoHidden: () => () => {} };
export const useSessionChrome = () => useContext(ChromeContext) || noopChrome;

/** Hide the video strip while the calling screen is mounted (e.g. the "starting size" step). */
export const useAutoHideVideo = (active = true) => {
  const { requestVideoHidden } = useSessionChrome();
  useEffect(() => (active ? requestVideoHidden() : undefined), [active, requestVideoHidden]);
};

/**
 * SessionActions - renders buttons into the nav row under the panel, to the right of End Session.
 *   <SessionActions onBack={...} primary={{ label: 'Next', onClick, disabled }} />
 *   <SessionActions><Button ...>Custom</Button></SessionActions>
 * Rendered in place when there is no SessionLayout above it.
 */
export const SessionActions = ({ onBack, backLabel = 'Back', backDisabled, primary, children }) => {
  const { actionsNode } = useSessionChrome();
  const content = (
    <>
      {children}
      {onBack && (
        <BackButton onClick={onBack} disabled={backDisabled}>
          {backLabel}
        </BackButton>
      )}
      {primary && (
        <NextButton onClick={primary.onClick} disabled={primary.disabled} data-testid='session-primary-action'>
          {primary.label || 'Next'}
        </NextButton>
      )}
    </>
  );
  return actionsNode ? createPortal(content, actionsNode) : <NavRow>{content}</NavRow>;
};

// ---------------------------------------------------------------------------------------------
// SessionLayout: the in-call / in-test layout = main panel + nav row + video strip.
//   portrait/phone: panel, nav row, then the strip (controls + 2 tiles) at the bottom
//   landscape:      video column on the left (~170px, 230px on desktop), panel + nav on the right
// ---------------------------------------------------------------------------------------------

const Grid = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  gap: 16px;
  ${({ mode, hidden }) =>
    mode === 'landscape'
      ? `grid-template-columns: ${hidden ? 'auto' : 'clamp(150px, 15vw, 230px)'} minmax(0, 1fr); grid-template-rows: minmax(0, 1fr);`
      : 'grid-template-columns: minmax(0, 1fr); grid-template-rows: minmax(0, 1fr) auto;'}
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
  min-width: 0;
  order: ${({ mode }) => (mode === 'landscape' ? 2 : 1)};
`;

const Side = styled.div`
  min-height: 0;
  min-width: 0;
  order: ${({ mode }) => (mode === 'landscape' ? 1 : 2)};
  display: flex;
  flex-direction: column;
`;

export const NavRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  .nav-right {
    display: flex;
    gap: 12px;
    margin-left: auto;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
  @media (max-width: ${breakpoints.phone - 1}px) {
    gap: 8px;
    .nav-right {
      gap: 8px;
    }
  }
`;

/**
 * SessionLayout
 * @param children        panel content
 * @param endAction       { label, onClick } for the red button at the left of the nav row (null to omit)
 * @param video           VideoStrip props: { localLabel, localName, remoteLabel, remoteName, order }
 * @param panelProps      extra props for <Panel/> (padding, cornerExtra)
 * @param showVideo       false to drop the strip entirely (e.g. consultation view)
 */
export const SessionLayout = ({ children, endAction, video, panelProps, showVideo = true }) => {
  const mode = useLayoutMode();
  const chrome = useSessionChrome();
  const hidden = chrome.videoHidden;
  const [slot, setSlot] = useState(null);
  const { setActionsNode } = chrome;
  useLayoutEffect(() => {
    setActionsNode(slot);
    return () => setActionsNode(null);
  }, [slot, setActionsNode]);

  return (
    <Grid mode={mode} hidden={hidden || !showVideo} data-layout={mode} data-testid='session-layout'>
      <Main mode={mode}>
        <Panel expanded={hidden} onToggleExpand={showVideo ? () => chrome.setVideoHidden(!chrome.userVideoHidden) : undefined} {...panelProps}>
          {children}
        </Panel>
        <NavRow data-testid='session-nav'>
          {endAction && <EndSessionButton onClick={endAction.onClick}>{endAction.label || 'End Session'}</EndSessionButton>}
          <div className='nav-right' ref={setSlot} />
        </NavRow>
      </Main>
      {showVideo && video && (
        <Side mode={mode}>
          <VideoStrip
            {...video}
            orientation={mode === 'landscape' ? 'column' : 'row'}
            tileHeight={mode === 'phone' ? '168px' : 'clamp(150px, 22vh, 252px)'}
            hidden={hidden}
            onToggleHidden={() => chrome.setVideoHidden(!chrome.userVideoHidden)}
          />
        </Side>
      )}
    </Grid>
  );
};

// ---------------------------------------------------------------------------------------------
// Frame - dev helper: renders children inside a fixed-size box matching a Figma frame.
// ---------------------------------------------------------------------------------------------

export const FrameBox = styled.div`
  width: ${({ w }) => w}px;
  height: ${({ h }) => h}px;
  overflow: auto;
  border: 1px solid ${colors.slate[200]};
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
`;

export default SessionLayout;
